/* eslint-disable @typescript-eslint/no-misused-promises */
import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import FormInput from '../../../components/FormElements/FormInput'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import type { IDistributionSeries } from '../../../models/DistributionSeries'
import { DistributionManager } from '../../../APIManager'
import { notification } from '../../../components/Notifications'

interface FormType {
  title: string
}

interface CreateAndEditDistributionSeriesProps {
  selectedProject?: IDistributionSeries
  handleCloseModal?: () => void
  returnDistributionSeries?: (series: IDistributionSeries) => void
}

export const CreateAndEditProject: React.FC<CreateAndEditDistributionSeriesProps> = ({
  selectedProject,
  handleCloseModal = () => { },
  returnDistributionSeries

}: CreateAndEditDistributionSeriesProps): JSX.Element => {
  const queryClient = useQueryClient()

  const theme = useTheme()
  const isEditing = selectedProject

  const navigate = useNavigate()

  const mutation = useMutation({
    mutationFn: async (seriesData: { title: string }) => {
      if (isEditing && selectedProject?.distributionSeriesId) {
        return await DistributionManager.updateProject(selectedProject.distributionSeriesId, seriesData)
      }
      return await DistributionManager.createProject(seriesData)
    },
    onSuccess: (result) => {
      handleCloseModal?.()
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['distributionPlan'] })
      notification(`Distribution series ${isEditing ? 'updated' : 'created'} successfully`, { variant: 'success' })
      const newSeries = result.data
      returnDistributionSeries?.(newSeries)
      if (!isEditing && newSeries?.distributionSeriesId) {
        navigate(`?projectId=${newSeries.distributionSeriesId}`)
      }
    },
    onError: (error: any) => {
      if (error?.message?.title) {
        notification(error?.message?.title?.[0] ?? 'Internal Server Error', { variant: 'error' })
      }
    }
  })

  const { title } = selectedProject ?? {}

  const distributionSeriesFormSchema = yup.object().shape({
    title: yup.string().required('Title is required')
  })

  const methods = useForm<FormType>({
    resolver: yupResolver(distributionSeriesFormSchema),
    defaultValues: {
      title: title ?? ''
    }
  })
  const { handleSubmit, control, formState: { isDirty, isSubmitting } } = methods

  const onSubmit: SubmitHandler<FormType> = async ({ title }) => {
    await mutation.mutateAsync({ title })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          name="title"
          label="Title"
          control={control}
          required
        />

        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditing ? 'Save' : 'Create',
            loading: mutation.isPending,
            type: 'submit',
            disabled: mutation.isPending || isSubmitting || (isEditing && !isDirty)
          }}
        />
      </form>
    </FormProvider>
  )
}

export default CreateAndEditProject
